/**
 * Where this build's sandbox fetches its packages (the VITE_SANDBOX_* sources), and what follows
 * from having none: no browser host is mounted (sandboxMount.ts) and the tab never reports itself
 * as a sandbox host candidate (C4), so the server never elects a tab that cannot run the sandbox.
 * Kept apart from sandboxMount.ts, which imports the agent client: the client reads this.
 */
import type { PackageSources } from "./sandbox/core/types";
import { browserCandidate, type CandidateWatcher } from "./sandbox/host/candidate";

/** Dev server only: a JSON PackageSources in localStorage stands in for the env (the e2e smoke). */
export const DEV_PACKAGES_KEY = "steddy:sandbox-packages";

type Env = Pick<
  ImportMetaEnv,
  "DEV" | "VITE_SANDBOX_PYODIDE_URL" | "VITE_SANDBOX_PYPI_URL" | "VITE_SANDBOX_NPM_URL"
>;

function devOverride(): PackageSources | null {
  try {
    const raw = localStorage.getItem(DEV_PACKAGES_KEY);
    const value = raw ? (JSON.parse(raw) as Partial<PackageSources>) : null;
    if (!value) return null;
    const { pyodideIndexURL, pypiIndexURL, npmRegistryURL } = value;
    return typeof pyodideIndexURL === "string" &&
      typeof pypiIndexURL === "string" &&
      typeof npmRegistryURL === "string"
      ? { pyodideIndexURL, pypiIndexURL, npmRegistryURL }
      : null;
  } catch {
    return null; // storage blocked or not JSON
  }
}

/** Where the sandbox's toolchain fetches packages, or null when this deployment has none. */
export function sandboxPackages(env: Env = import.meta.env): PackageSources | null {
  if (env.DEV) {
    const override = devOverride();
    if (override) return override;
  }
  const pyodideIndexURL = env.VITE_SANDBOX_PYODIDE_URL;
  const pypiIndexURL = env.VITE_SANDBOX_PYPI_URL;
  const npmRegistryURL = env.VITE_SANDBOX_NPM_URL;
  if (!pyodideIndexURL || !pypiIndexURL || !npmRegistryURL) return null;
  return { pyodideIndexURL, pypiIndexURL, npmRegistryURL };
}

/**
 * This tab's candidate watcher for the socket, or none when the build has no package sources:
 * hello then carries no `executor`, which the server treats as no report.
 */
export function sandboxCandidate(
  packages: PackageSources | null = sandboxPackages()
): CandidateWatcher | undefined {
  return packages ? browserCandidate() : undefined;
}
